import {
  Checkbox,
  Grid,
  GridColumn,
  GridRow,
  Input,
  ModalBottomButtons,
  Select,
  SelectOptionType,
} from '@bp/ui-components';
import { Form, Formik } from 'formik';
import { FormikHelpers } from 'formik/dist/types';
import { useTranslation } from 'react-i18next';
import { MultiValue, SingleValue } from 'react-select';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { useMemoizedClassNumberOptions } from '../../../hooks/useMemoizedClassNumberOptions';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import {
  FactorType,
  useCreateTeachingLoadFactorMutation,
  useGradeGroupsListQuery,
  useSubjectContainersListQuery,
  useSubjectsListQuery,
  useTeachingLoadFactorsQuery,
  useUpdateTeachingLoadFactorMutation,
  ValueMode,
} from '../../../types/planung-graphql-client-defs';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserErrorToast } from '../../../utils/toast';
import { FactorsFormType, TeachingLoadFactorType } from '../types/factorTypes';
import { interpreteAsNumber, submitFactor } from '../utils/factorUtils';
import { factorFormSchema } from './FactorForm.schema';

type FactorEpochFormProps = {
  selectedFactorUuid: string | null;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const FactorEpochForm = ({ selectedFactorUuid, onLoad, onClose }: FactorEpochFormProps) => {
  const { t } = useTranslation();
  const currentSchoolYear = useSelectedSchoolYear();
  const context = useMemorizedCacheTag('TEACHINGLOADFACTOR');

  const [, createTeachingLoadFactor] = useCreateTeachingLoadFactorMutation();
  const [, updateTeachingLoadFactor] = useUpdateTeachingLoadFactorMutation();

  const [{ data: factorsData }] = useTeachingLoadFactorsQuery({
    variables: { schoolYearUuid: currentSchoolYear?.uuid ?? '' },
    context,
    pause: !currentSchoolYear,
  });

  const selectedFactor: TeachingLoadFactorType | null =
    factorsData?.teachingLoadFactors.find((f) => f.uuid === selectedFactorUuid) ?? null;

  const [{ data: subjectsData }] = useSubjectsListQuery({
    context,
  });

  const [{ data: subjectContainersData }] = useSubjectContainersListQuery({ context });
  const [{ data: gradeGroupsData }] = useGradeGroupsListQuery({ context });

  const subjectContainers = subjectContainersData && subjectContainersData.subjectContainers;
  const subjects = subjectsData && subjectsData.subjects;
  const gradeGroups = gradeGroupsData && gradeGroupsData.gradeGroups;

  // select options
  const subjectsOptions = useCreateSelectOptions(subjects, 'uuid', 'name');
  const gradeGroupsOptions = useCreateSelectOptions(gradeGroups, 'uuid', 'name');
  const classNumberOptions = useMemoizedClassNumberOptions();
  const subjectContainersOptions = useCreateSelectOptions(subjectContainers, 'uuid', 'name');

  const handleSubmit = async (values: FactorsFormType, formikHelpers: FormikHelpers<FactorsFormType>) => {
    onLoad(true);

    const { error } = await submitFactor(
      values,
      selectedFactor,
      currentSchoolYear,
      context,
      updateTeachingLoadFactor,
      createTeachingLoadFactor,
    );

    if (error) {
      showUserErrorToast({ error });
    } else if (selectedFactor?.uuid) {
      showSuccessUpdateToast();
    } else {
      showSuccessCreateToast();
    }

    onLoad(false);
    formikHelpers.resetForm();
    onClose();
  };

  const initialValues: FactorsFormType = {
    uuid: selectedFactor?.uuid ?? null,
    factorType: FactorType.Time, // "Epoche"
    grades: selectedFactor?.grades ?? [],
    gradeGroups: selectedFactor?.gradeGroup?.map((item) => item.uuid),
    subjects: selectedFactor?.subject?.map((item) => item.uuid),
    subjectContainers: selectedFactor?.subjectContainer?.map((item) => item.uuid),
    value: selectedFactor?.value.toString() ?? '1',
    valueRaw: selectedFactor?.valueRaw ?? '1',
    valuePartial: selectedFactor?.valuePartial?.toString() ?? '1',
    valuePartialRaw: selectedFactor?.valuePartialRaw ?? '1',
    valueMode: selectedFactor?.valueMode ?? ValueMode.UnitCount, // default "Epoche"
    studentsMin: selectedFactor?.studentsMin ?? null,
    studentsMax: selectedFactor?.studentsMax ?? null,
    studentsPartialMin: selectedFactor?.studentsPartialMin ?? null,
    studentsPartialMax: selectedFactor?.studentsPartialMax ?? null,
    comment: selectedFactor?.comment ?? '',
    schoolYear: currentSchoolYear,
    active: selectedFactor?.active ?? true,
  };

  const factorsOptions: SelectOptionType[] = [
    { label: t('factors.byWeek'), value: ValueMode.Lesson },
    { label: t('factors.byEpoch'), value: ValueMode.UnitCount },
    { label: t('factors.bySchoolYear'), value: ValueMode.UnitDurationCount },
  ];

  return (
    <Formik<FactorsFormType>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={factorFormSchema}
      validateOnChange
      validateOnBlur
    >
      {({ values, handleChange, resetForm, dirty, isValidating, setFieldTouched, setFieldValue, errors }) => {
        const handleMultiSelectChange = async (name: keyof FactorsFormType, options: MultiValue<SelectOptionType>) => {
          await setFieldTouched(name, true);
          await setFieldValue(
            name,
            options.map((o) => o.value),
          );
        };

        return (
          <Form>
            <Grid useFormGap>
              <GridRow>
                <GridColumn width={11}>
                  <GridRow spacingBottom='s'>
                    <Select
                      placeholder={t('common.all')}
                      tooltipText={t('common.allIfNothingSelected')}
                      name='subjectContainers'
                      onChange={(o) => handleMultiSelectChange('subjectContainers', o as MultiValue<SelectOptionType>)}
                      options={subjectContainersOptions}
                      isSearchable
                      isClearable
                      isMulti
                      label={t('subjectContainer.title', { count: 2 })}
                      defaultValue={
                        selectedFactor?.subjectContainer
                          ? selectedFactor?.subjectContainer.map((item) => ({ label: item.name, value: item.uuid }))
                          : null
                      }
                    />
                  </GridRow>
                  <GridRow spacingBottom='s' spacingTop='s'>
                    <Select
                      placeholder={t('common.all')}
                      tooltipText={t('common.allIfNothingSelected')}
                      name='subjects'
                      onChange={async (options) =>
                        handleMultiSelectChange('subjects', options as MultiValue<SelectOptionType>)
                      }
                      options={subjectsOptions}
                      defaultValue={
                        selectedFactor?.subject
                          ? selectedFactor?.subject.map((item) => ({ label: item.name, value: item.uuid }))
                          : null
                      }
                      isSearchable
                      isClearable
                      isMulti
                      label={t('subject.title', { count: 2 })}
                    />
                  </GridRow>
                  <GridRow spacingTop='s'>
                    <GridColumn width={6}>
                      <Select
                        placeholder={t('common.all')}
                        tooltipText={t('common.allIfNothingSelected')}
                        name='grades'
                        onChange={async (options) =>
                          handleMultiSelectChange('grades', options as MultiValue<SelectOptionType>)
                        }
                        defaultValue={classNumberOptions.filter((opt) => values.grades.includes(opt.value))}
                        options={classNumberOptions}
                        isSearchable
                        isClearable
                        isMulti
                        label={t('grades.title', { count: 2 })}
                      />
                    </GridColumn>
                    <GridColumn width={6}>
                      <Select
                        placeholder={t('common.all')}
                        tooltipText={t('common.allIfNothingSelected')}
                        name='gradeGroups'
                        onChange={async (options) =>
                          handleMultiSelectChange('gradeGroups', options as MultiValue<SelectOptionType>)
                        }
                        options={gradeGroupsOptions}
                        isSearchable
                        isClearable
                        isMulti
                        label={t('gradeGroups.title', { count: 1 })}
                        defaultValue={
                          selectedFactor?.gradeGroup
                            ? selectedFactor?.gradeGroup.map((item) => ({ label: item.name, value: item.uuid }))
                            : null
                        }
                      />
                    </GridColumn>
                  </GridRow>
                </GridColumn>
                <GridColumn width={1} align='end'>
                  <Checkbox
                    className='mt-3'
                    checked={values.active}
                    name='active'
                    label={t('common.active.full')}
                    onChange={async (e) => {
                      await setFieldValue('active', e.target.checked);
                      await setFieldTouched('active', true);
                    }}
                  />
                </GridColumn>
              </GridRow>

              <GridRow headline={t('factors.title', { count: 1 })}>
                <GridColumn width={11} offset='right'>
                  <GridRow spacingBottom='s'>
                    <GridColumn width={3}>
                      <Input
                        tooltipText={t('factors.tooltip')}
                        value={values.valueRaw}
                        name='value'
                        onChange={async (e) => {
                          await setFieldValue('valueRaw', e.target.value);
                          await setFieldValue('value', interpreteAsNumber(e.target.value));
                          await setFieldTouched('value');
                        }}
                        label={t('factors.whole')}
                        error={errors.valueRaw}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        name='value'
                        tooltipText={t('factors.valueWillBeUsed')}
                        readonly
                        label={t('factors.calculationValueWhole')}
                        value={values.value}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        name='studentsMin'
                        label={t('factors.minStudentsWhole')}
                        tooltipText={t('factors.minStudensTooltip')}
                        placeholder='-'
                        value={values.studentsMin}
                        onChange={handleChange}
                        error={errors.studentsMin}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        name='studentsMax'
                        label={t('factors.maxStudentsWhole')}
                        tooltipText={t('factors.maxStudensTooltip')}
                        placeholder='-'
                        value={values.studentsMax}
                        onChange={handleChange}
                        error={errors.studentsMax}
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow spacingBottom='s' spacingTop='s'>
                    <GridColumn width={3}>
                      <Input
                        tooltipText={t('factors.tooltip')}
                        value={values.valuePartialRaw}
                        name='value'
                        onChange={async (e) => {
                          await setFieldValue('valuePartialRaw', e.target.value);
                          await setFieldValue('valuePartial', interpreteAsNumber(e.target.value));
                          await setFieldTouched('valuePartial');
                        }}
                        label={t('factors.partial')}
                        error={errors.valuePartialRaw}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        name='value'
                        tooltipText={t('factors.valueWillBeUsed')}
                        readonly
                        label={t('factors.calculationValuePartial')}
                        value={values.valuePartial}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        name='studentsPartialMin'
                        label={t('factors.minStudentsPartial')}
                        tooltipText={t('factors.minStudensTooltip')}
                        placeholder='-'
                        value={values.studentsPartialMin}
                        onChange={handleChange}
                        error={errors.studentsPartialMin}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        name='studentsPartialMax'
                        label={t('factors.maxStudentsPartial')}
                        tooltipText={t('factors.maxStudensTooltip')}
                        placeholder='-'
                        value={values.studentsPartialMax}
                        onChange={handleChange}
                        error={errors.studentsPartialMax}
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow spacingTop='s'>
                    <GridColumn width={6}>
                      <Select
                        name='valueMode'
                        onChange={async (options) => {
                          const opt = options as SingleValue<SelectOptionType>;
                          await setFieldTouched('valueMode', true);
                          await setFieldValue('valueMode', opt?.value);
                        }}
                        options={factorsOptions}
                        value={factorsOptions.find((fo) => fo.value === values.valueMode)}
                        label={t('factors.valueMode')}
                        error={errors.valueMode}
                      />
                    </GridColumn>
                    <GridColumn width={6}>
                      <Input
                        name='comment'
                        onChange={handleChange}
                        label={t('common.comment')}
                        value={values.comment ?? ''}
                        error={errors.comment}
                      />
                    </GridColumn>
                  </GridRow>
                </GridColumn>
              </GridRow>
            </Grid>

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              submitButton={{
                disabled: !dirty || isValidating || Object.keys(errors).length > 0,
              }}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
